import { Params } from "@angular/router";
import { AzureSuggestsResult } from "../../azure-search/azure-search";
import { AttachmentFacet } from "./facets/attachment-facet.model";
import { CountValueFacet } from "./facets/count-value-facet.model";
import { CountValueFacetItem } from "./facets/facet.model";
import { GroupFacet } from "./facets/group-facet.model";
import { LocationFacet } from "./facets/location-facet.model";
import { MapFacet } from "./facets/map-facet.model";
import { SliderFacet, SliderBounds } from "./facets/slider-facet.model";
import { PrimaryTypeFacet } from "./facets/primary-type-facet.model";
import { OrderBy } from "./order-by.model";
import { BoolFacet } from "./facets/bool-facet.model";

export class SearchRequest {
  currentLocationSearch: boolean = false;
  searchTerm: string = "";
  selectedSuggestion: AzureSuggestsResult;
  orderBy: OrderBy = new OrderBy();
  pageIndex: number = 0;

  // facets
  location: LocationFacet = new LocationFacet("location");
  facetsSetup: boolean = false;
  map: MapFacet;
  primaryType: PrimaryTypeFacet;
  typeDefinition: CountValueFacet;
  rpoEnabled: BoolFacet;
  buyItNowEnabled: BoolFacet;
  isTopOwner: BoolFacet;
  make: CountValueFacet;
  model: CountValueFacet;
  hours: SliderFacet;
  enginePower: SliderFacet;
  year: SliderFacet;
  weight: SliderFacet;
  attachment: AttachmentFacet;
  account: CountValueFacet;
  excludeAccountId: CountValueFacet;
  // end of facets

  initialFilter =
    "((status eq 'Available' or status eq 'Onboarding') or ((status eq 'OnRent' or status eq 'Reserved') and buyItNowEnabled eq true)) and " +
    "(rentalRate ne 0) and " +
    "(requiresAdminApproval eq false) and " +
    "(approvalStatus eq 'Approved' or approvalStatus eq null)";

  constructor() {
    this.map = new MapFacet("map");
  }

  initializeFacets(newFacets: any): void {
    this.facetsSetup ? this.updateFacets(newFacets) : this.setupfacets(newFacets);
  }

  private static get yearBounds(): SliderBounds {
    return {
      min: 2000,
      max: new Date().getFullYear() + 2,
      includeInvalidInFilter: true,
    };
  }

  private filterValidYearFacets(facets: CountValueFacetItem[]): CountValueFacetItem[] {
    const { min: minYear, max: maxYear } = SearchRequest.yearBounds;
    const valid = (facets || []).filter(f => {
      const year = +f.value;
      return !isNaN(year) && year >= minYear && year <= maxYear;
    });

    if (valid.length === 0) {
      const minItem = new CountValueFacetItem();
      minItem.value = minYear;
      minItem.label = String(minYear);
      minItem.count = 0;
      minItem.checked = false;
      const maxItem = new CountValueFacetItem();
      maxItem.value = maxYear;
      maxItem.label = String(maxYear);
      maxItem.count = 0;
      maxItem.checked = false;
      return [minItem, maxItem];
    }
    return valid;
  }

  private setupfacets(facets: any) {
    this.primaryType = new PrimaryTypeFacet("primaryType", facets.primaryType || []);
    this.typeDefinition = new CountValueFacet("typeDefinition", facets.typeDefinition || []);
    this.rpoEnabled = new BoolFacet("rpoEnabled", facets.rpoEnabled || [], "Rent to Own");
    this.buyItNowEnabled = new BoolFacet("buyItNowEnabled", facets.buyItNowEnabled || [], "Buy It Now");
    this.isTopOwner = new BoolFacet("isTopOwner", facets.isTopOwner || [], "Top Owner");
    this.make = new CountValueFacet("make", facets.make || []);
    this.model = new CountValueFacet("model", facets.model || []);
    this.hours = new SliderFacet("hours", "hrs", 100, facets.hours || []);
    this.enginePower = new SliderFacet("enginePower", "hp", 1, facets.enginePower || []);
    this.year = new SliderFacet("year", "", 1, this.filterValidYearFacets(facets.year || []), SearchRequest.yearBounds);
    this.weight = new SliderFacet("weight", "lbs", 1000, facets.weight || []);
    this.attachment = new AttachmentFacet("attachment", facets["relatedAttachments/typeDefinition"], facets["relatedAttachments/size"]);
    this.account = new CountValueFacet("accountId", [], "Account");
    this.excludeAccountId = new CountValueFacet("excludeAccountId", [], "Exclude Account");

    this.facetsSetup = true;
  }

  private updateFacets(newFacets) {
    this.primaryType.update(newFacets.primaryType);
    // this.typeDefinition.update(newFacets.typeDefinition);
    this.rpoEnabled.update(newFacets.rpoEnabled);
    this.buyItNowEnabled.update(newFacets.buyItNowEnabled);
    this.isTopOwner.update(newFacets.isTopOwner);
    if (this.make.filterForMake() == null) {
      this.make.update(newFacets.make);
    }
    this.model.update(newFacets.model);
    this.hours.update(newFacets.hours);
    this.year.update(this.filterValidYearFacets(newFacets.year || []));
    this.enginePower.update(newFacets.enginePower);
    this.weight.update(newFacets.weight);
    this.attachment.update(newFacets["relatedAttachments/typeDefinition"], newFacets["relatedAttachments/size"]);
  }

  newsearchFilter(excludePrimaryTimeAndMake: boolean = false): string {
    const urlParams = new URLSearchParams(window.location.search);

    this.account = new CountValueFacet("accountId", [], "Account");
    this.excludeAccountId = new CountValueFacet("excludeAccountId", [], "Exclude Account");
    if (this.primaryType?.selected != true) {
      this.primaryType = new PrimaryTypeFacet("primaryType", []);
    }
    if (this.make?.filterForMake() == null) {
      this.make = new CountValueFacet("make", []);
    }

    return [
      this.location?.newFilter(this.location.location),
      this.account?.filter(urlParams),
      this.excludeAccountId.filter(urlParams),
      excludePrimaryTimeAndMake ? null : this.primaryType?.filter(),
      excludePrimaryTimeAndMake ? null : this.make?.filterForMake(),
      this.initialFilter,
      ...this.routeSpecificFilters(),
    ]
      .filter(d => d != null)
      .join(" and ");
  }

  filter(excludeMake: boolean = false): string {
    const urlParams = new URLSearchParams(window.location.search);
    // need to fix this shit
    return [
      this.location?.newFilter(this.location.location),
      this.map?.filter(urlParams),
      this.primaryType?.filter(),
      this.typeDefinition?.filter(urlParams),
      this.rpoEnabled?.filter(urlParams),
      this.buyItNowEnabled?.filter(urlParams),
      this.isTopOwner?.filter(urlParams),
      excludeMake ? null : this.make?.filterForMake() || this.make?.filter(urlParams),
      this.model?.filter(urlParams),
      this.hours?.filter(urlParams),
      this.year?.filter(urlParams),
      this.enginePower?.filter(urlParams),
      this.weight?.filter(urlParams),
      this.attachment?.filter(urlParams),
      this.account?.filter(urlParams),
      this.excludeAccountId?.filter(urlParams),
      this.initialFilter,
      ...this.routeSpecificFilters(),
    ]
      .filter(d => d != null)
      .join(" and ");
  }

  get skip(): number {
    let page: number = 0;
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams) {
      page = +urlParams.get("page");
      this.pageIndex = page;
    }

    return page * 17;
  }

  get order(): string {
    // Use selectedOrderBy directly instead of reading from query param
    // The query param will be updated after navigation, but we need the value NOW
    let orderBy: string = this.orderBy.selectedOrderBy;

    // If orderBy is still null, try to get it from query param (for initial page load)
    if (orderBy === null || orderBy === undefined) {
      orderBy = this.orderBy.getFromQueryParam();
    }

    if (orderBy == "sortlocation" || orderBy == null) {
      if (this.location.selected) {
        orderBy = `geo.distance(location/point, geography'POINT(${this.location.location.longitude} ${this.location.location.latitude})') asc`;
      } else {
        orderBy = "createdDateTime desc";
      }
    }

    return orderBy;
  }

  toQueryParam() {
    const queryParams: Params = {};
    queryParams["radius"] = this.location.toQueryParam();
    queryParams["primaryType"] = this.primaryType.toQueryParam();
    queryParams["typeDefinition"] = this.typeDefinition.toQueryParam();
    queryParams["rpoEnabled"] = this.rpoEnabled.toQueryParam();
    queryParams["buyItNowEnabled"] = this.buyItNowEnabled.toQueryParam();
    queryParams["isTopOwner"] = this.isTopOwner.toQueryParam();
    queryParams["make"] = this.make.toQueryParam();
    queryParams["model"] = this.model.toQueryParam();
    queryParams["hours"] = this.hours.toQueryParam();
    queryParams["year"] = this.year.toQueryParam();
    queryParams["enginePower"] = this.enginePower.toQueryParam();
    queryParams["weight"] = this.weight.toQueryParam();
    const [attachmentCatalogQueryParam, attachmentSpecQueryParam] = this.attachment.toQueryParam();
    queryParams["attachmentCatalog"] = attachmentCatalogQueryParam;
    queryParams["attachmentSpec"] = attachmentSpecQueryParam;
    queryParams["orderBy"] = this.orderBy.selectedOrderBy || undefined;
    return queryParams;
  }

  // when we change the machine type we want to kill the children facets (make, model, etc....)
  onPrimaryTypeChange(type?: string) {
    this.primaryType.facets = type ? ([{ label: type, count: 1, value: type, checked: true }] as CountValueFacetItem[]) : [];
    this.primaryType.selectedPrimaryType = type ? type : "Any";
    this.typeDefinition.clear();
    this.onTypeDefinitionChange();
  }

  onTypeDefinitionChange() {
    this.make.clear();
    this.onMakeChange();
  }

  onMakeChange() {
    this.model.clear();
    this.onModelChange();
  }

  onModelChange() {
    this.enginePower.clear();
    this.attachment.clear();
    this.weight.clear();
  }

  clearFilters() {
    // this.typeDefinition?.uncheck();
    // this.make?.uncheck();
    // this.primaryType?.uncheck();
    this.rpoEnabled?.uncheck();
    this.buyItNowEnabled?.uncheck();
    this.isTopOwner?.uncheck();
    this.model?.uncheck();
    this.attachment?.uncheck();
    this.hours?.uncheck();
    this.enginePower?.uncheck();
    this.year?.uncheck();
    this.weight?.uncheck();
    this.orderBy.clear();
  }

  get facetSelectedCountForSearching(): number {
    let count = [
      this.typeDefinition,
      this.primaryType,
      this.rpoEnabled,
      this.buyItNowEnabled,
      this.isTopOwner,
      this.make,
      this.model,
      this.hours,
      this.year,
      this.enginePower,
      this.attachment,
    ].filter(d => d.selected).length;

    if (this.orderBy.selectedOrderBy !== null) {
      count++;
    }
    return count;
  }

  get facetSelectedCount(): number {
    let count = [
      // this.typeDefinition,
      // this.primaryType,
      // this.make,
      this.rpoEnabled,
      this.buyItNowEnabled,
      this.isTopOwner,
      this.model,
      this.hours,
      this.year,
      this.enginePower,
      this.attachment,
    ].filter(d => d.selected).length;

    if (this.orderBy.selectedOrderBy !== null) {
      count++;
    }
    return count;
  }

  get userSearchedWithSuggestion(): boolean {
    return this.selectedSuggestion != null && this.searchTerm === this.selectedSuggestion.value;
  }

  searchResultsUrl(): string {
    this.facetsSetup = false;
    this.clearFilters();

    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const baseRoute = this.getBaseRouteFromSegments(pathSegments);

    // Determine the search results URL - default to equipment-rentals if not on a valid search route
    let searchResultsUrl = "equipment-rentals";
    if (baseRoute === "equipment-rentals" || baseRoute === "equipment-for-sale" || baseRoute.endsWith("-map")) {
      searchResultsUrl = baseRoute.replace("-map", ""); // Use current route, strip -map if present
    }

    let locationQueryParam = "";
    if (this.location.selected) {
      locationQueryParam = `/${this.location.toCityStateSeo()}`;
    }

    if (!this.searchTerm) {
      this.searchTerm = "all";
    }

    if (!this.userSearchedWithSuggestion || (this.selectedSuggestion != null && this.selectedSuggestion.autoFacetValues.length === 0)) {
      const test = `${searchResultsUrl}/all${locationQueryParam}`;
      return test;
    }

    for (const facetValue of this.selectedSuggestion.autoFacetValues.slice(0, 2)) {
      searchResultsUrl += `/${facetValue}`;
    }

    return searchResultsUrl;
  }

  // dont like this implementation but it works
  swapView() {
    this.map.clear();
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const baseRoute = this.getBaseRouteFromSegments(pathSegments);
    // Check for map routes: legacy /map, nested /equipment-rentals/map, or flat equipment-rentals-map
    const isMapRoute = baseRoute === "map" || baseRoute.endsWith("-map") || (pathSegments.length > 1 && pathSegments[1] === "map");
    let searchResultsUrl = isMapRoute ? this.getListRouteFromBase(baseRoute, pathSegments) : this.getMapRouteFromBase(baseRoute);
    const pathParts = this.getPathPartsForSwap(pathSegments, isMapRoute);

    if (pathParts.length > 0) {
      searchResultsUrl += `/${pathParts.map(d => decodeURIComponent(d)).join("/")}`;
    } else {
      searchResultsUrl += `/${decodeURIComponent(this.searchTerm)}`;
    }
    return searchResultsUrl;
  }

  private getBaseRouteFromSegments(pathSegments: string[]): string {
    if (!pathSegments.length) {
      return "equipment-rentals";
    }

    if (pathSegments[0] === "map") {
      return "map";
    }

    // Handle flat map routes (equipment-rentals-map, equipment-for-sale-map)
    if (pathSegments[0].endsWith("-map")) {
      return pathSegments[0];
    }

    return pathSegments[0];
  }

  private getListRouteFromBase(baseRoute: string, pathSegments: string[]): string {
    if (baseRoute === "map") {
      return "equipment-rentals";
    }

    // Remove -map suffix if present
    if (baseRoute.endsWith("-map")) {
      return baseRoute.replace("-map", "");
    }

    return baseRoute;
  }

  private getMapRouteFromBase(baseRoute: string): string {
    if (baseRoute === "map") {
      return "equipment-rentals";
    }

    // Use flat route structure with -map suffix
    return `${baseRoute}-map`;
  }

  private getPathPartsForSwap(pathSegments: string[], isMapRoute: boolean): string[] {
    if (!pathSegments.length) {
      return [];
    }

    if (isMapRoute) {
      if (pathSegments[0] === "map") {
        return pathSegments.slice(1);
      }
      // For flat map routes (equipment-rentals-map), path parts start at index 1
      return pathSegments.slice(1);
    }

    if (pathSegments[0] === "map") {
      return pathSegments.slice(1);
    }

    return pathSegments.slice(1);
  }

  private routeSpecificFilters(): string[] {
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    if (!pathSegments.length) {
      return [];
    }

    const base = pathSegments[0];
    const filters: string[] = [];
    const urlParams = new URLSearchParams(window.location.search);

    // Only apply route-based filter if there's no explicit query param override
    if (base === "equipment-for-sale") {
      const buyItNowParam = urlParams.get("buyItNowEnabled");
      // Only apply route default if param is null (not explicitly set)
      if (buyItNowParam === null) {
        filters.push("buyItNowEnabled eq true");
      }
      // If param is "false", don't apply filter (user unchecked it)
      // If param is "true", filter will be applied via BoolFacet.filter()
    }

    if (base === "equipment-rent-to-own") {
      const rpoParam = urlParams.get("rpoEnabled");
      // Only apply route default if param is null (not explicitly set)
      if (rpoParam === null) {
        filters.push("rpoEnabled eq true");
      }
      // If param is "false", don't apply filter (user unchecked it)
      // If param is "true", filter will be applied via BoolFacet.filter()
    }

    return filters;
  }

  get queryParams() {
    const queryParams = {};
    const urlParams = new URLSearchParams(window.location.search);
    const searchTerm = urlParams.get("q");
    if (searchTerm) {
      queryParams["q"] = searchTerm;
    }
    const excludeAccountId = urlParams.get("excludeAccountId");
    if (excludeAccountId) {
      queryParams["excludeAccountId"] = excludeAccountId;
    }

    const accountId = urlParams.get("accountId");
    if (accountId) {
      queryParams["accountId"] = accountId;
    }

    return queryParams;
  }
}
